import { useState, useEffect, useCallback } from 'react';
import { theaterReservationService } from '../../shared/services';
import reservationService from '../../shared/services/reservationService';

export const useExistingReservations = (studentId, eventId, isAdminContext = false) => {
  const [existingReservations, setExistingReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Función para cargar las reservas existentes del Alumno
  const loadExistingReservations = useCallback(async () => {
    if (!studentId) {
      setExistingReservations([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const hasAdminToken = !!localStorage.getItem('adminToken');
      if (hasAdminToken) {
        // Importante: enviar solo studentId para que el backend aplique el branch correcto
        const reservations = await reservationService.getAllReservations({ studentId });
        const filtered = eventId ? reservations.filter(r => r.eventId === eventId) : reservations;
        setExistingReservations(filtered);
      } else {
        // Contexto teatro
        if (!eventId) {
          setExistingReservations([]);
        } else {
          const reservations = await theaterReservationService.getReservationsByStudent(studentId, eventId);
          setExistingReservations(reservations);
        }
      }
    } catch (error) {
      console.error('Error al cargar reservas existentes:', error);
      setError(error.message || 'Error al cargar reservas existentes');
      setExistingReservations([]);
    } finally {
      setLoading(false);
    }
  }, [studentId, eventId]);

  useEffect(() => {
    loadExistingReservations();
  }, [loadExistingReservations]);

  const getExistingSeatIds = useCallback(() => {
    if (isAdminContext) {
      // Contexto admin: puede modificar tanto 'pending' como 'sold'
      return existingReservations
        .filter(reservation => reservation.status === 'pending' || reservation.status === 'sold')
        .map(reservation => reservation.compositeId);
    } else {
      // Contexto teatro: solo puede modificar 'pending'
      return existingReservations
        .filter(reservation => reservation.status === 'pending')
        .map(reservation => reservation.compositeId);
    }
  }, [existingReservations, isAdminContext]);

  const getTotalExistingSeats = useCallback(() => {
    const countableStatuses = new Set(['pending', 'sold', 'confirmed', 'completed']);
    return existingReservations
      .filter(reservation => countableStatuses.has(reservation.status))
      .length;
  }, [existingReservations]);

  const getTotalExistingPrice = useCallback(() => {
    return existingReservations
      .filter(reservation => reservation.status !== 'cancelled')
      .reduce((total, reservation) => total + (reservation.price || 0), 0);
  }, [existingReservations]);

  const hasActiveReservations = useCallback(() => {
    if (isAdminContext) {
      // Contexto admin: puede modificar tanto 'pending' como 'sold'
      return existingReservations.some(reservation => reservation.status === 'pending' || reservation.status === 'sold');
    } else {
      // Contexto teatro: solo puede modificar 'pending'
      return existingReservations.some(reservation => reservation.status === 'pending');
    }
  }, [existingReservations, isAdminContext]);

  return {
    existingReservations,
    loading,
    error,
    loadExistingReservations,
    getExistingSeatIds,
    getTotalExistingSeats,
    getTotalExistingPrice,
    hasActiveReservations,
  };
}; 